// LoginPage — comitê. Passo 1: credenciais (e-mail+senha+TOTP). Passo 2: liveness
// (ADR-0009 Peça 6, vídeo + desafio server-side) — substitui a captura face-api antiga.
//
// A derivação de KEK_membro (D-190, necessária para decifrar DEKs dos casos) roda em
// PARALELO no passo 2 e é independente da biometria. Só entramos no rito quando AMBOS:
// liveness liberou (token elevado aplicado) E KEK pronta.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  loginWithPassword,
  getStoredSession,
  drawLivenessChallenge,
  verifyLiveness,
} from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
import { deriveKekMembro, storeKekMembro, loadKekMembro } from '../lib/kek-member-session';
import { backfillMemberKeypair } from '../lib/enroll-keypair-client';
import { Alert, Button, Card, Field, Input, LivenessCapture } from '@aions/ui-primitives';

type Step = 'credentials' | 'liveness';

export function LoginPage({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // DEV-ONLY — pré-preenche o TOTP no `vite dev` (o backend dispensa o código com
  // TOTP_DEV_BYPASS). MODE==='development' é o `vite dev`; produção ('production')
  // e testes ('test', vitest) ficam com o campo vazio.
  const [totp, setTotp] = useState(import.meta.env.MODE === 'development' ? '000000' : '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [kekReady, setKekReady] = useState(false);
  const [livenessOk, setLivenessOk] = useState(false);
  // passwordRef evita race no double-effect do React 18 strict-mode.
  const passwordRef = useRef<string>('');

  const handleCredentialsSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    passwordRef.current = password;
    const result = await loginWithPassword(email, password, totp);
    setLoading(false);
    if (!result.ok || !result.session) {
      setError(result.error ?? 'invalid_credentials');
      return;
    }
    setSession(result.session);
    setStep('liveness');
  };

  // 🔴 INVERTIDO EM 24/08 — o reconhecimento facial passa a valer TAMBÉM em `vite dev`.
  //
  // Antes, `MODE === 'development'` bastava para dar o selo sem câmera. O efeito colateral
  // era que o caminho mais frágil do sistema — permissão de mídia, enquadramento, o veredito
  // do serviço externo — nunca era exercitado por quem desenvolve, e só aparecia em produção
  // ou numa cerimônia presencial, que é o pior lugar para descobrir.
  //
  // O escape hatch continua existindo, mas agora é ESCOLHA EXPLÍCITA: `VITE_DEV_SKIP_LIVENESS=true`
  // no `.env.local` do app. A diferença não é cosmética — antes o padrão era pular e era preciso
  // saber do bypass para desconfiar dele; agora o padrão é a defesa e pular exige um ato.
  //
  // ⚠️ Continua valendo só em `vite dev`. Em produção o MODE é 'production' e em teste é 'test',
  // então nenhum dos dois olha para esta variável — a suíte sempre renderizou a captura real.
  // 🔴 UMA fonte de verdade. A regra vivia em DUAS cópias — este efeito e o ternário do JSX
  // lá embaixo — e em 24/08 eu troquei só uma: o selo deixou de ser dado automaticamente e o
  // componente que o daria continuou não sendo renderizado. O login ficava preso para sempre
  // numa tela dizendo "Liveness dispensado… Finalizando o acesso", sem nunca finalizar, e sem
  // uma única requisição saindo. Duas cópias de uma regra é o defeito; a constante é a cura.
  const pularLivenessEmDev =
    import.meta.env.MODE === 'development' && import.meta.env.VITE_DEV_SKIP_LIVENESS === 'true';

  useEffect(() => {
    if (step !== 'liveness') return;
    if (pularLivenessEmDev) setLivenessOk(true);
  }, [step, pularLivenessEmDev]);

  // KEK_membro: deriva assim que entra no passo liveness (overlap com a câmera).
  useEffect(() => {
    if (step !== 'liveness' || !session) return;
    const salt = session.user.kekSalt;
    if (!salt) {
      setError('missing_kek_salt');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const kekRaw = await deriveKekMembro(passwordRef.current, salt);
        try {
          await storeKekMembro(kekRaw);
        } finally {
          kekRaw.fill(0); // D-191 — zera KEK raw após o store
        }
        // 🔴 O BACKFILL DA KEYPAIR SAIU DAQUI (24/08) e foi para o efeito abaixo, que só
        // roda DEPOIS do liveness. Motivo: `/enroll-keypair` passou a exigir o selo
        // biométrico, e aqui o token ainda é o NÃO-elevado — manter o backfill neste
        // ponto faria todo primeiro login devolver 403 e o membro entrar sem keypair,
        // silenciosamente (a lib é não-fatal por desenho).
        //
        // A derivação da KEK CONTINUA em paralelo com a câmera: ela é Argon2id, é o
        // passo caro, e não depende de biometria. O que passou a ser sequencial é só a
        // chamada de rede que o gate protege.
        if (!cancelled) setKekReady(true);
      } catch {
        if (!cancelled) setError('kek_derive_failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, session]);

  // Entra no rito só quando liveness liberou E KEK pronta. Re-lê a sessão do storage
  // (auth-client já trocou o token pelo elevado com o selo) e sincroniza o contexto.
  useEffect(() => {
    if (!livenessOk || !kekReady) return;
    const elevated = getStoredSession();
    if (elevated) setSession(elevated);

    // ADR-0010 Fatia 1 — backfill do keypair, agora DEPOIS do liveness, porque a rota
    // exige o selo biométrico. O token elevado já está no storage (o `auth-client` o
    // trocou ao liberar), então a chamada sai autorizada.
    //
    // Continua NÃO-FATAL e não bloqueia o `onSuccess`: se falhar, o membro entra sem
    // keypair e o próximo login re-tenta (o servidor é first-writer-wins). Aguardar
    // aqui travaria a entrada no rito por uma auto-cura — foi assim que se decidiu
    // quando ele nasceu, e o gate novo não muda essa parte.
    if (session && !session.user.comiteKeypairEnrolled) {
      void (async () => {
        try {
          const kekRaw = await loadKekMembro();
          try {
            await backfillMemberKeypair(kekRaw).catch(() => undefined);
          } finally {
            kekRaw.fill(0); // D-191
          }
        } catch {
          // KEK indisponível nesta sessão — o próximo login re-tenta.
        }
      })();
    }

    onSuccess();
  }, [livenessOk, kekReady, session, setSession, onSuccess]);

  // ---------------- Passo 1: credenciais ----------------
  if (step === 'credentials') {
    return (
      <div style={{ maxWidth: 440, margin: '40px auto' }}>
        <Card>
          <h2>{t('login.title')}</h2>

          {error && (
            <Alert variant="error" style={{ marginBottom: 16 }}>
              {t(`login.errors.${error}`, { defaultValue: t('login.errors.invalid_credentials') })}
            </Alert>
          )}

          <form onSubmit={(e) => void handleCredentialsSubmit(e)}>
            <Field label={t('login.email_label')}>
              {({ id }) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}
            </Field>

            <Field label={t('login.password_label')}>
              {({ id }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={12}
                />
              )}
            </Field>

            <Field label={t('login.totp_label')} help={t('login.totp_help')}>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  mono
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  pattern="[0-9]{6}"
                  maxLength={6}
                  style={{ letterSpacing: '0.4em' }}
                />
              )}
            </Field>

            <Button type="submit" block loading={loading}>
              {t('login.submit')}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // ---------------- Passo 2: liveness ----------------
  return (
    <div style={{ maxWidth: 440, margin: '40px auto' }}>
      {(error === 'kek_derive_failed' || error === 'missing_kek_salt') && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          Falha ao preparar o acesso seguro (chave de sessão). Saia e entre novamente.
        </Alert>
      )}
      {pularLivenessEmDev ? (
        <Alert variant="info" role="status" style={{ marginBottom: 12 }}>
          Liveness dispensado (VITE_DEV_SKIP_LIVENESS). Finalizando o acesso…
        </Alert>
      ) : (
        <LivenessCapture
          onVerified={() => setLivenessOk(true)}
          drawChallenge={drawLivenessChallenge}
          verify={verifyLiveness}
        />
      )}
      {livenessOk && !kekReady && (
        <Alert variant="info" role="status" style={{ marginTop: 12 }}>
          Finalizando o acesso seguro…
        </Alert>
      )}
    </div>
  );
}
